import { FormAData } from '@/components/forms/kyc/FormA';
import type { IdentificationFormData } from '@/components/forms/kyc/identification';
import {
  ControllingOwner as FormKControllingOwner,
  FormKData,
} from '@/components/forms/kyc/types/formK.types';
import { convertStringToBoolean, formatDateForApi } from '@/lib/utils';
import type { KycFormData as ApiKycFormData } from '@/types';

// Local component form data interfaces (imported from actual components)
export interface LocalKycFormData {
  identification?: IdentificationFormData;
  formA?: FormAData;
  formK?: FormKData;
  formS?: Record<string, unknown>;
  formT?: Record<string, unknown>;
  communicationLanguage?: 'en' | 'de' | 'fr' | 'it';
}

/**
 * Interface representing an executive/authorized representative.
 * Matches the structure expected by the identification form.
 *
 * @property {string} firstName - Executive's first name (required)
 * @property {string} lastName - Executive's last name (required)
 * @property {string} position - Executive's position/title (required)
 * @property {string} address - Executive's residential address (required)
 * @property {string} postcode - Postal/ZIP code (required)
 * @property {string} city - City of residence (required)
 * @property {string} country - Country code (ISO 3166-1 alpha-2, required)
 * @property {string} nationality - Nationality country code (required)
 * @property {Date} [birthDate] - Date of birth (optional)
 */
export interface Executive {
  firstName: string;
  lastName: string;
  position: string;
  address: string;
  postcode: string;
  city: string;
  country: string;
  nationality: string;
  birthDate?: Date;
}

/**
 * Interface representing a beneficial owner.
 * Used in Form A for ownership information.
 *
 * @property {string} firstName - Owner's first name (required)
 * @property {string} lastName - Owner's last name (required)
 * @property {string} address - Owner's residential address (required)
 * @property {string} postcode - Postal/ZIP code (required)
 * @property {string} city - City of residence (required)
 * @property {string} country - Country code (ISO 3166-1 alpha-2, required)
 * @property {string} nationality - Nationality country code (required)
 * @property {Date} [birthDate] - Date of birth (optional)
 * @property {number} [ownershipPercentage] - Percentage of ownership (optional)
 */
export interface BeneficialOwner {
  firstName: string;
  lastName: string;
  address: string;
  postcode: string;
  city: string;
  country: string;
  nationality: string;
  birthDate?: Date;
  ownershipPercentage?: number;
}

/**
 * Interface representing a controlling owner.
 * Used in Form K for control structure information.
 *
 * @property {string} firstName - Owner's first name (required)
 * @property {string} lastName - Owner's last name (required)
 * @property {string} address - Owner's residential address (required)
 * @property {string} postalCode - Postal/ZIP code (required)
 * @property {string} country - Country code (ISO 3166-1 alpha-2, required)
 * @property {string} nationality - Nationality country code (required)
 * @property {string} [companyName] - Associated company name if applicable (optional)
 * @property {Date} [birthDate] - Date of birth (optional)
 */
export interface ControllingOwner {
  firstName: string;
  lastName: string;
  address: string;
  postalCode: string;
  country: string;
  nationality: string;
  companyName?: string;
  birthDate?: Date;
}

/**
 * Adapter function to convert local component form data to API format.
 * Maps the component's form structure to the backend API requirements.
 *
 * This adapter handles:
 * - Identification form data (company details, executives, legal information)
 * - Form A data (beneficial owners and their origin of assets)
 * - Form K data (controlling owners and their origin of assets)
 *
 * When forms are hidden (not in requiredFormIds), their fields are explicitly
 * set to null to signal the backend to clear those values.
 *
 * @param token - The KYC token for this submission
 * @param localFormData - Form data from the components
 * @param requiredFormIds - Array of required form IDs (e.g., ['identification', 'form-k', 'form-a'])
 * @returns API-formatted KYC form data ready for submission
 * @throws {Error} If required fields are missing or invalid
 *
 * @example
 * ```typescript
 * const apiData = adaptKycFormDataForApi('token123', {
 *   identification: identificationFormData,
 *   formA: formAData,
 *   formK: formKData
 * }, ['identification', 'form-k']);
 * ```
 */
export function adaptKycFormDataForApi(
  token: string,
  localFormData: LocalKycFormData,
  requiredFormIds: string[] = ['identification', 'form-k', 'form-a']
): ApiKycFormData {
  // Determine which forms are required (visible)
  const isFormKRequired = requiredFormIds.includes('form-k');
  const isFormARequired = requiredFormIds.includes('form-a');
  // Input validation with detailed error messages
  if (!token?.trim()) {
    throw new Error(
      'KYC token is required and cannot be empty. Please provide a valid token.'
    );
  }

  if (!localFormData) {
    throw new Error(
      'Form data is required for API submission. Please provide form data.'
    );
  }

  const identification = localFormData.identification;

  if (!identification) {
    throw new Error(
      'Identification form data is required. Please complete the identification section before submitting.'
    );
  }

  // Extract data with immutability - create new arrays to prevent mutations
  const executives = identification.executives
    ? [...identification.executives]
    : [];

  const beneficialOwners = localFormData.formA?.beneficialOwners
    ? [...localFormData.formA.beneficialOwners]
    : [];

  const controllingOwners = localFormData.formK?.controllingOwners
    ? [...localFormData.formK.controllingOwners]
    : [];

  // Build API payload with explicit field mapping
  return {
    // Core identification fields
    token,
    company_name: identification.companyName,
    head_office_address: identification.headOfficeAddress,
    postcode: identification.postcode,
    city: identification.city,
    country: identification.country,
    legal_form_type_of_company: identification.legalForm,
    legal_form_type_of_company_other:
      identification.legalFormOther || undefined,

    // Financial intermediary information
    financial_intermediary: convertStringToBoolean(
      identification.isFinancialIntermediary
    ),
    type_of_financial_intermediary: identification.financialIntermediaryType,
    type_of_financial_intermediary_other:
      identification.financialIntermediaryTypeOther || undefined,
    special_legal_supervision_name:
      identification.supervisoryOrganization || undefined,

    // Listed company information
    listed_company: convertStringToBoolean(identification.listedCompany),
    stock_exchange_listing: identification.stockExchange || undefined,
    ticker_symbol: identification.tickerSymbol || undefined,

    // Subsidiary of listed company
    part_of_listed_company: convertStringToBoolean(
      identification.subsidiaryOwnership
    ),
    parent_stock_exchange_listing:
      identification.parentCompanyExchange || undefined,
    parent_ticker_symbol: identification.parentTicker || undefined,

    // Shareholders over 25% - derived from Form K controlling owner type
    // True when controlling owner determination is 'shares_25_percent'
    shareholders_over_25_percent: isFormKRequired
      ? localFormData.formK?.controllingOwnerType === 'shares_25_percent'
      : null,

    // Company details
    number_of_employees: identification.employees
      ? Number(identification.employees)
      : undefined,
    shell_company: convertStringToBoolean(identification.shellCompany) ?? false,
    business_activities_description:
      identification.businessDescription || undefined,

    // Operational office information
    same_address_as_contracting_party:
      identification.sameAddressAsContractingParty || false,
    operational_office_company_name:
      identification.operationalOfficeCompanyName || undefined,
    operational_office_address:
      identification.operationalOfficeAddress || undefined,
    operational_office_postcode:
      identification.operationalOfficePostcode || undefined,
    operational_office_city: identification.operationalOfficeCity || undefined,
    operational_office_country:
      identification.operationalOfficeCountry || undefined,

    // Map executives data with proper field transformation
    executives: executives.map(exec => ({
      first_name: exec.firstName,
      last_name: exec.lastName,
      position: exec.position,
      birth_date: formatDateForApi(exec.birthDate),
      nationality: exec.nationality,
      address: exec.address,
      postcode: exec.postcode,
      city: exec.city,
      country: exec.country,
    })),

    // Form K - Controlling owners and asset origin
    // When Form K is hidden, explicitly send null values to clear backend data
    controlling_owner_origin_assets: isFormKRequired
      ? localFormData.formK?.originOfAssets || undefined
      : null,
    controlling_owner_origin_other: isFormKRequired
      ? localFormData.formK?.originOfAssetsOther || undefined
      : null,
    controlling_owner_determination: isFormKRequired
      ? localFormData.formK?.controllingOwnerType || undefined
      : null,
    controlling_owners: isFormKRequired
      ? controllingOwners.map((owner: FormKControllingOwner) => ({
          first_name: owner.firstName,
          last_name: owner.lastName,
          address: owner.address,
          postal_code: owner.postalCode,
          city: owner.city,
          country: owner.country,
          nationality: owner.nationality,
          birth_date: formatDateForApi(owner.birthDate),
        }))
      : [],
    shareholders_legal_entities: isFormKRequired
      ? convertStringToBoolean(localFormData.formK?.shareholdersOtherCompanies)
      : null,
    third_parties_beneficial_owners: isFormKRequired
      ? convertStringToBoolean(localFormData.formK?.fiduciaryHolding)
      : null,

    // Form A - Beneficial owners and asset origin
    // When Form A is hidden, explicitly send null values to clear backend data
    beneficial_owner_origin_assets: isFormARequired
      ? localFormData.formA?.originOfAssets || undefined
      : null,
    beneficial_owner_origin_other: isFormARequired
      ? localFormData.formA?.originOfAssetsOther || undefined
      : null,
    beneficial_owner_identification: isFormARequired
      ? localFormData.formA?.beneficialOwnerType || undefined
      : null,
    beneficial_owners: isFormARequired
      ? beneficialOwners.map(owner => ({
          first_name: owner.firstName,
          last_name: owner.lastName,
          address: owner.address || undefined,
          postcode: owner.postcode || undefined,
          city: owner.city || undefined,
          country: owner.country || undefined,
          birth_date: formatDateForApi(owner.birthDate),
          nationality: owner.nationality,
        }))
      : [],

    // Politically Exposed Persons (PEP)
    is_pep_involved: convertStringToBoolean(identification.isPepInvolved),
    politically_exposed_persons: identification.politicallyExposedPersons?.map(
      pep => ({
        first_name: pep.firstName,
        last_name: pep.lastName,
        pep_reason: pep.pepReason,
      })
    ),

    // Communication language preference
    communication_language: localFormData.communicationLanguage || undefined,
  };
}

/**
 * Parses a date string from API response into a Date object.
 * Handles ISO 8601 date formats and YYYY-MM-DD formats.
 *
 * @param dateString - Date string from API (ISO 8601 or YYYY-MM-DD)
 * @returns Date object or undefined if invalid/empty
 */
function parseDateFromApi(dateString: string | undefined): Date | undefined {
  if (!dateString) return undefined;
  const parsed = new Date(dateString);
  return isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * Converts a boolean or string value from API to a 'yes'/'no' string for form fields.
 * Handles both boolean values and string values that the API might return.
 *
 * @param value - Boolean or string value from API (can be null when form is hidden)
 * @returns 'yes' if true, 'no' if false, undefined if undefined or null
 */
function convertBooleanToString(
  value: boolean | string | null | undefined
): 'yes' | 'no' | undefined {
  if (value === undefined || value === null) return undefined;

  // If already a string, ensure it's lowercase and return it
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'yes' || lowerValue === 'no') {
      return lowerValue as 'yes' | 'no';
    }
    // Handle 'true'/'false' strings from API
    if (lowerValue === 'true') return 'yes';
    if (lowerValue === 'false') return 'no';
    return undefined;
  }

  // Handle boolean values
  return value ? 'yes' : 'no';
}

/**
 * Adapter function to convert API response data to local component form data.
 * This is the reverse operation of adaptKycFormDataForApi.
 * Used for pre-filling forms with existing data (admin edit mode).
 *
 * @param apiFormData - Form data from API response
 * @returns Local form data structure for component consumption
 *
 * @example
 * ```typescript
 * const localData = adaptApiKycFormDataToLocal(apiResponse.form_data);
 * // Use localData to pre-fill the form components
 * ```
 */
export function adaptApiKycFormDataToLocal(
  apiFormData: ApiKycFormData
): LocalKycFormData {
  if (!apiFormData) {
    return {};
  }

  // Build identification form data
  const identification = {
    // Core company details
    companyName: apiFormData.company_name || '',
    headOfficeAddress: apiFormData.head_office_address || '',
    postcode: apiFormData.postcode || '',
    city: apiFormData.city || '',
    country: apiFormData.country || '',
    legalForm: apiFormData.legal_form_type_of_company || '',
    legalFormOther: apiFormData.legal_form_type_of_company_other || '',

    // Financial intermediary information
    isFinancialIntermediary: convertBooleanToString(
      apiFormData.financial_intermediary
    ),
    financialIntermediaryType:
      apiFormData.type_of_financial_intermediary || undefined,
    financialIntermediaryTypeOther:
      apiFormData.type_of_financial_intermediary_other || undefined,
    supervisoryOrganization:
      apiFormData.special_legal_supervision_name || undefined,

    // Listed company information
    listedCompany: convertBooleanToString(apiFormData.listed_company),
    stockExchange: apiFormData.stock_exchange_listing || undefined,
    tickerSymbol: apiFormData.ticker_symbol || undefined,

    // Subsidiary of listed company
    subsidiaryOwnership: convertBooleanToString(
      apiFormData.part_of_listed_company
    ),
    parentCompanyExchange:
      apiFormData.parent_stock_exchange_listing || undefined,
    parentTicker: apiFormData.parent_ticker_symbol || undefined,

    // Company details
    employees: apiFormData.number_of_employees?.toString() || '',
    shellCompany: convertBooleanToString(apiFormData.shell_company),
    businessDescription:
      apiFormData.business_activities_description || undefined,

    // Operational office information
    sameAddressAsContractingParty:
      apiFormData.same_address_as_contracting_party || false,
    operationalOfficeCompanyName:
      apiFormData.operational_office_company_name || undefined,
    operationalOfficeAddress:
      apiFormData.operational_office_address || undefined,
    operationalOfficePostcode:
      apiFormData.operational_office_postcode || undefined,
    operationalOfficeCity: apiFormData.operational_office_city || undefined,
    operationalOfficeCountry:
      apiFormData.operational_office_country || undefined,

    // Map executives data
    executives: (apiFormData.executives || []).map(exec => ({
      firstName: exec.first_name || '',
      lastName: exec.last_name || '',
      position: exec.position || '',
      birthDate: parseDateFromApi(exec.date_of_birth),
      nationality: exec.nationality || '',
      address: exec.address || '',
      postcode: exec.postcode || '',
      city: exec.city || '',
      country: exec.country || '',
    })),

    // Politically Exposed Persons (PEP)
    isPepInvolved: convertBooleanToString(apiFormData.is_pep_involved),
    politicallyExposedPersons: (
      apiFormData.politically_exposed_persons || []
    ).map(pep => ({
      firstName: pep.first_name || '',
      lastName: pep.last_name || '',
      pepReason: pep.pep_reason || '',
    })),
  };

  // Build Form K data (controlling owners)
  const formK =
    apiFormData.controlling_owners?.length ||
    apiFormData.controlling_owner_origin_assets?.length
      ? {
          originOfAssets: apiFormData.controlling_owner_origin_assets || [],
          originOfAssetsOther:
            apiFormData.controlling_owner_origin_other || undefined,
          controllingOwnerType:
            apiFormData.controlling_owner_determination || undefined,
          shareholdersOtherCompanies: convertBooleanToString(
            apiFormData.shareholders_legal_entities
          ),
          fiduciaryHolding: convertBooleanToString(
            apiFormData.third_parties_beneficial_owners
          ),
          controllingOwners: (apiFormData.controlling_owners || []).map(
            owner => ({
              firstName: owner.first_name || '',
              lastName: owner.last_name || '',
              address: owner.address || '',
              postalCode: owner.postcode || '',
              city: owner.city || '',
              country: owner.country || '',
              nationality: owner.nationality || '',
              companyName: owner.company_name || '',
              birthDate: parseDateFromApi(
                owner.birth_date || owner.date_of_birth
              ),
            })
          ),
        }
      : undefined;

  // Build Form A data (beneficial owners)
  const formA =
    apiFormData.beneficial_owners?.length ||
    apiFormData.beneficial_owner_origin_assets?.length
      ? {
          originOfAssets: apiFormData.beneficial_owner_origin_assets || [],
          originOfAssetsOther:
            apiFormData.beneficial_owner_origin_other || undefined,
          beneficialOwnerType:
            apiFormData.beneficial_owner_identification || undefined,
          beneficialOwners: (apiFormData.beneficial_owners || []).map(
            owner => ({
              firstName: owner.first_name || '',
              lastName: owner.last_name || '',
              address: owner.address || '',
              postcode: owner.postcode || '',
              city: owner.city || '',
              country: owner.country || '',
              birthDate: parseDateFromApi(owner.date_of_birth),
              nationality: owner.nationality || '',
              ownershipPercentage: owner.ownership_percentage,
            })
          ),
        }
      : undefined;

  return {
    identification: identification as unknown as IdentificationFormData,
    formK: formK as unknown as FormKData,
    formA: formA as unknown as FormAData,
    communicationLanguage: apiFormData.communication_language as
      'en' | 'de' | 'fr' | 'it' | undefined,
  };
}
